import { effect, Injectable, type Signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {ContextFromSignal} from '../../models/context-from-signal';
import {UserDetails} from '../../models/user-details';

declare global {
  interface Window {
    data_access: {
      user_context: {
        logged_in_role: Signal<ContextFromSignal>;
        selected_role: Signal<ContextFromSignal>;
      };
      auth_context: {
        identity_claims: Signal<IdentityClaims>;
      };
    };
  }
}


export interface IdentityClaims {
  'https://momentum.co.za/idn': string;
  'https://momentum.co.za/idt': string;
  'https://momentum.co.za/idc': string;
  'https://momentum.co.za/uid': string;
  'https://momentum.co.za/company_registration': string;
  'https://momentum.co.za/groups': string[];
  'https://momentum.co.za/business_keys': {
    business_key: string;
    business_type: number;
  }[];
  'https://momentum.co.za/party_key': string;
  given_name: string;
  family_name: string;
  nickname: string;
  preferred_username: string;
  name: string;
  picture: string;
  gender: 'M' | 'F';
  birthdate: string;
  updated_at: string;
  email: string;
  email_verified: boolean;
  phone_number: string;
  iss: string;
  aud: string;
  iat: number;
  exp: number;
  sub: string;
  sid: string;
  nonce: string;
}

const EmptyUserDetails: UserDetails = {
  given_name: '',
  fsp: '',
  contextKey: '',
  groupKey: '',
  businessAccountKey: '',
  subjectId: '',
  role: ''
};

@Injectable({ providedIn: 'root' })
export class UserService {
  private claimsDetails: UserDetails | undefined;

  private loggedInRoleSignal: Signal<ContextFromSignal> | undefined;
  private identityClaimsSignal: Signal<IdentityClaims> | undefined;
  public loggedInRole$ = new BehaviorSubject<UserDetails>(EmptyUserDetails);
  public loggedInRole: UserDetails | undefined;

  constructor() {
    // Initialize signals if available
    if (window.data_access?.user_context) {
      this.loggedInRoleSignal = window.data_access.user_context
        .logged_in_role as Signal<ContextFromSignal>;
    }
    if (window.data_access?.auth_context) {
      this.identityClaimsSignal = window.data_access.auth_context
        .identity_claims as Signal<IdentityClaims>;
    }

    // Setup effects in injection context
    effect(() => {
      if (this.identityClaimsSignal) {
        this.claimsDetails = { given_name: this.identityClaimsSignal().given_name } as UserDetails;
      }
      if (!this.loggedInRoleSignal) return;
      const Role: ContextFromSignal = this.loggedInRoleSignal();
      this.setRoleObservable(Role);
    });
  }

  private setRoleObservable(role: ContextFromSignal): void {
    const updatedLoggedInRole = this.addClaims({
      fsp: role.fsp === 'MC' ? 'CONSULT' : role.fsp,
      contextKey: `${role.code1}-${role.code2}`,
      groupKey: role.groupKey,
      businessAccountKey: role.businessAccountKey,
      subjectId: role.subjectId,
      role: role.role
    } as UserDetails);

    if (updatedLoggedInRole.fsp !== '') {
      this.loggedInRole = updatedLoggedInRole;
      this.loggedInRole$.next(updatedLoggedInRole);
    }
  }

  private addClaims(userDetails: UserDetails): UserDetails {
    return Object.assign({}, this.claimsDetails, userDetails) as UserDetails;
  }
}
